// src/components/Login Components/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { FiSun, FiMoon, FiMail, FiLock, FiEye, FiEyeOff, FiAlertTriangle, FiTrendingUp, FiUsers, FiZap, FiGlobe, FiAward } from 'react-icons/fi';
import NeonCity from '../CityFX/NeonCity';
import WalletAuthButton from '../WalletSecurity/WalletAuthButton';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
// El login por wallet solo se ofrece si hay App ID de Privy configurado.
const WALLET_ENABLED = !!process.env.REACT_APP_PRIVY_APP_ID;

// Franja de valor (honesta, on-strategy) en vez de métricas infladas.
const LIVE_STATS = [
  { text: 'Tu plata en tu wallet — nadie la custodia', Icon: FiLock },
  { text: 'Solo vendedores verificados por invitación', Icon: FiUsers },
  { text: 'Tu wallet es tu identidad — sin trámites eternos', Icon: FiZap },
  { text: 'Operá en USDT y USDC · Base y BNB Chain', Icon: FiTrendingUp },
  { text: '16 países conectados', Icon: FiGlobe },
  { text: 'Trades P2P en minutos, a tu precio', Icon: FiAward },
];

const CRYPTO_SYMBOLS = ['₿', 'Ξ', '◎', '◈', '₮', '⬡'];

function GamingBackground() {
  return (
    <div className="lg-bg" aria-hidden="true">
      {/* Capas cyberpunk: estrellas + grilla synthwave + skyline (solo tema oscuro) */}
      <NeonCity prefix="lg" />

      {/* Ambient orbs */}
      <div className="lg-orb lg-orb-1" />
      <div className="lg-orb lg-orb-2" />
      <div className="lg-orb lg-orb-3" />

      {/* Floating particles */}
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={`p-${i}`}
          className="lg-particle"
          style={{
            left: `${Math.random() * 100}%`,
            animationDuration: `${6 + Math.random() * 10}s`,
            animationDelay: `${Math.random() * 8}s`,
            width: `${1.5 + Math.random() * 2.5}px`,
            height: `${1.5 + Math.random() * 2.5}px`,
            opacity: 0.15 + Math.random() * 0.35,
          }}
        />
      ))}

      {/* Floating crypto symbols */}
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={`c-${i}`}
          className="lg-crypto-float"
          style={{
            left: `${8 + (i * 12) % 85}%`,
            animationDuration: `${14 + Math.random() * 12}s`,
            animationDelay: `${i * 1.8}s`,
            fontSize: `${0.7 + Math.random() * 0.6}rem`,
          }}
        >
          {CRYPTO_SYMBOLS[i % CRYPTO_SYMBOLS.length]}
        </div>
      ))}
    </div>
  );
}

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export default function Login() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const { login }   = useAuth();
  // A dónde volver tras loguear: la ruta que el usuario intentaba abrir (ProtectedRoute
  // guarda `state.from`), o un destino por ?next=, o el home por defecto.
  const params  = new URLSearchParams(location.search);
  const fromLoc = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : null;
  const redirectTo = fromLoc || params.get('next') || '/';

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [statIdx, setStatIdx]   = useState(0);
  const [btcPrice, setBtcPrice] = useState(null);
  const [btcDir, setBtcDir]     = useState(0); // 1 = sube, -1 = baja, 0 = sin cambio

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('bitplify_theme');
    return saved ? saved === 'dark' : true;
  });

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('bitplify_theme', next ? 'dark' : 'light');
    document.body.classList.toggle('dark-mode', next);
  };

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    const t = setInterval(() => setStatIdx(i => (i + 1) % LIVE_STATS.length), 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let prev = null;
    const fetchBtc = async () => {
      try {
        const res  = await fetch('https://data-api.binance.vision/api/v3/ticker/price?symbol=BTCUSDT');
        const data = await res.json();
        const raw  = parseFloat(data.price);
        if (prev !== null && raw !== prev) setBtcDir(raw > prev ? 1 : -1);
        prev = raw;
        setBtcPrice(raw.toLocaleString('en-US', { maximumFractionDigits: 0 }));
      } catch { setBtcPrice(null); }
    };
    fetchBtc();
    const t = setInterval(fetchBtc, 15000);
    return () => clearInterval(t);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Completa todos los campos.'); return; }
    if (!validateEmail(email)) { setError('Email inválido. Verifica tu dirección.'); return; }
    if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `Error ${res.status}`);
      }
      const data = await res.json();
      if (data.token) {
        login(data.user ?? { email }, data.token);
        navigate(redirectTo, { replace: true });
      } else {
        setError('Credenciales incorrectas. Intenta de nuevo.');
      }
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError('No se pudo conectar al servidor. Intenta más tarde.');
      } else {
        setError(err.message || 'Error al iniciar sesión.');
      }
    } finally {
      setLoading(false);
    }
  };

  const StatIcon = LIVE_STATS[statIdx].Icon;

  return (
    <div className={`lg-wrapper ${darkMode ? 'dark' : 'light'}`}>
      <GamingBackground />

      <button className="lg-theme-toggle" onClick={toggleTheme} title={darkMode ? 'Modo claro' : 'Modo oscuro'}>
        <span className="lg-theme-icon">
          {darkMode ? <FiSun /> : <FiMoon />}
        </span>
      </button>

      <div className="lg-left">
        <div className="lg-brand">
          <img src="/assets/Logo-White.png" alt="Bitplify" className="lg-logo lg-logo-dark" />
          <img src="/assets/Logo.png"       alt="Bitplify" className="lg-logo lg-logo-light" />
          <span className="lg-beta">Beta</span>
        </div>

        <div className="lg-hero">
          <h1 className="lg-hero-title">El mercado sos vos,<br />no el exchange que te pone precio.</h1>
          <p className="lg-hero-sub">Intercambiá P2P en LATAM. Tu plata en tu wallet, tu precio, tus reglas.</p>
        </div>

        <div className="lg-stats-live">
          <div className="lg-stat-ticker">
            <span className="lg-stat-dot" />
            <span className="lg-stat-text" key={statIdx}>
              <StatIcon className="lg-stat-icon" />
              {LIVE_STATS[statIdx].text}
            </span>
          </div>
          {btcPrice && (
            <div className="lg-btc-pill">
              <span className="lg-live-dot" />
              <span className="fin-label lg-btc-label">BTC/USDT</span>
              <span className="fin-num lg-btc-price">${btcPrice}</span>
              <span className={`fin-delta lg-btc-delta ${btcDir < 0 ? 'fin-down' : 'fin-up'}`}>
                {btcDir < 0 ? '▼' : '▲'}
              </span>
            </div>
          )}
        </div>

        <div className="lg-mini-board">
          <span className="lg-mini-title"><FiAward className="lg-mini-title-icon" /> Top traders hoy</span>
          <div className="lg-mini-list">
            {[
              { name: 'SiliconHash', rank: 1, pnl: '+18.4%' },
              { name: 'BinanceHero', rank: 2, pnl: '+12.1%' },
              { name: 'NeonMadrid',  rank: 3, pnl: '+9.7%'  },
            ].map((t) => (
              <div key={t.name} className="lg-mini-row">
                <span className={`fin-rank fin-rank--${t.rank} lg-mini-rank fin-num`}>{t.rank}</span>
                <span className="lg-mini-name">@{t.name}</span>
                <span className="fin-num fin-delta fin-up lg-mini-pnl">{t.pnl}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="lg-left-footer"><FiTrendingUp className="lg-footer-icon" /> Intercambia crypto P2P. Gana XP. Domina el mercado.</p>
      </div>

      <div className="lg-right">
        <div className="lg-card">

          <div className="lg-mobile-brand">
            <img src="/assets/Logo-White.png" alt="Bitplify" className="lg-mobile-logo lg-logo-dark" />
            <img src="/assets/Logo.png"       alt="Bitplify" className="lg-mobile-logo lg-logo-light" />
            <span className="lg-beta">Beta</span>
          </div>

          <div className="lg-card-header">
            <h2 className="lg-card-title">Bienvenido de vuelta</h2>
            <p className="lg-card-sub">Ingresa a tu cuenta para intercambiar</p>
          </div>

          <form onSubmit={handleSubmit} className="lg-form">

            <div className="lg-field">
              <label className="lg-label"><FiMail className="lg-label-icon" /> Email</label>
              <input
                type="email"
                className="lg-input"
                placeholder="trader@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            <div className="lg-field">
              <div className="lg-label-row">
                <label className="lg-label"><FiLock className="lg-label-icon" /> Contraseña</label>
                <a href="/forgot" className="lg-forgot">¿Olvidaste tu contraseña?</a>
              </div>
              <div className="lg-pw-wrap">
                <input
                  type={showPw ? 'text' : 'password'}
                  className="lg-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button type="button" className="lg-pw-toggle" onClick={() => setShowPw(p => !p)} tabIndex={-1}>
                  {showPw ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            {error && (
              <div className="lg-error">
                <FiAlertTriangle className="lg-error-icon" />
                {error}
              </div>
            )}

            <button type="submit" className="lg-btn-primary" disabled={loading}>
              {loading ? <span className="lg-spinner" /> : 'Ingresar'}
            </button>

          </form>

          {/* Alternativa: autocustodia con wallet (secundario) */}
          {WALLET_ENABLED && (
            <div className="lg-wallet">
              <div className="lg-divider"><span>o entrá con tu wallet</span></div>
              <WalletAuthButton
                label="Entrar con wallet"
                onSuccess={() => navigate(redirectTo, { replace: true })}
              />
            </div>
          )}

          <p className="lg-register-link">
            ¿No tienes cuenta? <Link to="/register">Regístrate gratis →</Link>
          </p>

        </div>
      </div>
    </div>
  );
}
